import {Component, HostListener, ViewChild, ElementRef, AfterViewInit, OnInit, input, Input, Inject, LOCALE_ID} from '@angular/core';
import {CommonModule} from '@angular/common';
import {gsap} from 'gsap';
import {Helper} from '../../shared/helper/helper';
import Role from '../../shared/enums/role.enum';
import {SharedModule} from '../../shared/shared.module';
import Swiper from 'swiper';
import {Subscription} from 'rxjs';
import {SubscriptionService} from '../../core/services/subscription.service';
import {AuthService} from '../../core/services';

interface SubscriptionFeature {
    id: string;
    title: string;
    description: string;
    includedIn: {
        standard: boolean;
        advance: boolean;
    };
}

interface SubscriptionPlan {
    type: string;
    title: string;
    subtitle?: string;
    price: string;
    priceUnit: string;
    ctaText: string;
    ctaLink: string;
}

interface SubscriptionType {
    name: string;
    features: SubscriptionFeature[];
    plans: {
        standard: SubscriptionPlan;
        advance: SubscriptionPlan;
    };
}

@Component({
    selector: 'app-subscription-notification',
    standalone: true,
    imports: [CommonModule, SharedModule],
    templateUrl: './subscription-notification.component.html',
    styleUrls: ['./subscription-notification.component.scss']
})
export class SubscriptionNotificationComponent implements AfterViewInit, OnInit {
    @ViewChild('standardFactsSwiper') standardFactsSwiper?: ElementRef;
    @ViewChild('advanceFactsSwiper') advanceFactsSwiper?: ElementRef;
    @ViewChild('overlayContainer') overlayContainer?: ElementRef;

    // ElementRefs für die Content-Container
    @ViewChild('standardContent') standardContent?: ElementRef;
    @ViewChild('advanceContent') advanceContent?: ElementRef;
    @Input() type: string = 'buyer';
    selectedFeatureId: string | null = null;
    currentPlan: 'standard' | 'advance' | null = null;
    standardSwiperInstance: any = null;
    advanceSwiperInstance: any = null;
    // Separater Zustand für jede Box
    showStandardFeatureDetails = false;
    showAdvanceFeatureDetails = false;
    // Timeline für Animationen
    mainTimeline: gsap.core.Timeline | null = null;
    // Animation bereits ausgeführt?
    animationExecuted = false;
    subscriptionTypes: { [key: string]: SubscriptionType } = {
        buyer: {
            name: 'buyer',
            features: [
                {
                    id: 'standort-expose',
                    title: $localize`Standort Exposé`,
                    description: $localize`Präsentiere dein Unternehmen im exklusiven B2B-Netzwerk, und erreiche gezielt neue Partner.`,
                    includedIn: {standard: true, advance: true}
                },
                {
                    id: 'praesenz-b2b',
                    title: $localize`Präsenz im B2B Netzwerk`,
                    description: $localize`Dein Unternehmensprofil ist im Netzwerk gelistet und kann von Premium-Mitgliedern gefunden und kontaktiert werden.`,
                    includedIn: {standard: true, advance: true}
                },
                {
                    id: 'supplier-zugang',
                    title: $localize`Unlimitierter Supplier Zugang`,
                    description: $localize`Entdecke und kontaktiere Top-Supplier der Luxusreisebranche – ohne Einschränkungen. (Unterkünfte, Veranstalter, DMC, DMO, NTO, Airlines, Cruiselines, ...)`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'fam-trips',
                    title: $localize`FAM Trips`,
                    description: $localize`Sichere dir deinen Platz bei exklusiven Inforeisen weltweit und erlebe Luxusprodukte hautnah. Zugang zu exklusiven weltweiten FAM Trips.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'smart-booking',
                    title: $localize`SMART Booking`,
                    description: $localize`Profitiere von exklusiven Buchungsvorteilen, höheren Provisionen und erhalte zusätzlich VIP-Extras für deine Kunden.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'spotlight-news',
                    title: $localize`Spotlight News`,
                    description: $localize`Erhalte exklusive Branchen-News, Trends und Insider Angebote News aus der Welt der Luxusreiseindustrie.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'support',
                    title: $localize`Support`,
                    description: $localize`Onboarding-Guide & Support`,
                    includedIn: {standard: true, advance: false}
                },
                {
                    id: 'support-advanced',
                    title: $localize`Premium-Support`,
                    description: $localize`Priorisierter Support und individuelle Unterstützung für einen überzeugenden Unternehmensauftritt.`,
                    includedIn: {standard: false, advance: true}
                }
            ],
            plans: {
                standard: {
                    type: 'standard',
                    title: 'Loop Plus',
                    price: '0 Euro',
                    priceUnit: $localize`pro Monat`,
                    ctaText: $localize`Weiter`,
                    ctaLink: 'javascript:;'
                },
                advance: {
                    type: 'advance',
                    title: 'Loop Plus',
                    subtitle: 'Advance',
                    price: $localize`ab` + ' 19,00 Euro',
                    priceUnit: $localize`pro Monat`,
                    ctaText: $localize`Jetzt 30 Tage kostenlos testen`,
                    ctaLink: '/' + this.locale + '/subscription/booking'
                }
            }
        },
        supplier: {
            name: 'supplier',
            features: [
                {
                    id: 'standort-expose',
                    title: $localize`Standort Exposé`,
                    description: $localize`Präsentiere dein Unternehmen im exklusiven B2B-Netzwerk, und erreiche gezielt neue Partner.`,
                    includedIn: {standard: true, advance: true}
                },
                {
                    id: 'praesenz-b2b-network',
                    title: $localize`Präsenz im B2B Netzwerk`,
                    description: $localize`Dein Unternehmensprofil ist im Netzwerk gelistet und kann von Premium-Mitgliedern gefunden und kontaktiert werden.`,
                    includedIn: {standard: true, advance: true}
                },
                {
                    id: 'unlimitierter-buyer-zugang',
                    title: $localize`Unlimitierter Buyer Zugang`,
                    description: $localize`Entdecke und kontaktiere Top-Buyer der Luxusreisebranche ohne Einschränkungen (Reisebüros, Veranstalter, MICE, Journalisten, ...).`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'fam-trip',
                    title: $localize`FAM Trip`,
                    description: $localize`Stelle dein Unternehmen handverlesenen Top-Buyern bei deinen exklusiven FAM Trips persönlich vor. Stärke deine Geschäftsbeziehungen und positioniere deine Marke gezielt im Luxusreise-Segment.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'smart-booking',
                    title: $localize`SMART Booking`,
                    description: $localize`Biete ausgewählten Reiseprofis exklusive Buchungsvorteile, höhere Provisionen und VIP-Extras. Maximiere deine Reichweite und Umsätze auch ohne GDS-Anbindung. Behalte die volle Kontrolle ohne Nutzung teurer Buchungsportale.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'spotlight-news',
                    title: $localize`Spotlight News`,
                    description: $localize`Leistungsstarkes Marketing-Tool für deinen Vertrieb: Veröffentliche Unternehmens-News, exklusive Angebote und Insider-Informationen direkt an die relevanten Partner der Luxus Branche.`,
                    includedIn: {standard: false, advance: true}
                },
                {
                    id: 'support',
                    title: $localize`Support`,
                    description: $localize`Onboarding-Guide & Support`,
                    includedIn: {standard: true, advance: false}
                },
                {
                    id: 'support-advanced',
                    title: $localize`Premium-Support`,
                    description: $localize`Priorisierter Support und individuelle Unterstützung für einen überzeugenden Unternehmensauftritt.`,
                    includedIn: {standard: false, advance: true}
                }
            ],
            plans: {
                standard: {
                    type: 'standard',
                    title: 'Loop Plus',
                    price: '0 Euro',
                    priceUnit: $localize`pro Monat`,
                    ctaText: $localize`Weiter`,
                    ctaLink: 'javascript:;'
                },
                advance: {
                    type: 'advance',
                    title: 'Loop Plus',
                    subtitle: 'Advance',
                    price: $localize`ab` + ' 29,00 Euro',
                    priceUnit: $localize`pro Monat`,
                    ctaText: $localize`Jetzt 30 Tage kostenlos testen`,
                    ctaLink: '/' + this.locale + '/subscription/booking'
                }
            }
        }
    };
    private subscription = new Subscription();

    constructor(
        private helper: Helper,
        public subscriptionService: SubscriptionService,
        private authService: AuthService,  // Hinzufügen
        @Inject(LOCALE_ID) public locale: string
    ) {
    }

    // Getter für den aktuellen Subscription-Typ
    get currentSubscriptionType(): SubscriptionType {
        return this.subscriptionTypes[this.type];
    }

    // Getter für die aktuellen Features
    get features(): SubscriptionFeature[] {
        return this.currentSubscriptionType.features;
    }

    // Getter für die Pläne des aktuellen Typs
    get plans(): { standard: SubscriptionPlan; advance: SubscriptionPlan } {
        return this.currentSubscriptionType.plans;
    }

    // Filtert Features nach Plan-Typ
    getIncludedFeatures(planType: 'standard' | 'advance'): SubscriptionFeature[] {
        return this.features.filter(feature => feature.includedIn[planType]);
    }

    // Schließen mit Escape-Taste
    @HostListener('document:keydown.escape')
    onEscapePress() {
        this.closeNotification();
    }

    closeNotification(): void {
        // Timeline rückwärts abspielen, wenn vorhanden
        if (this.mainTimeline) {
            this.mainTimeline.reverse().eventCallback('onReverseComplete', () => {
                document.body.classList.remove('p-overflow-hidden');
                this.hideFeatureDetails('standard');
                this.hideFeatureDetails('advance');
                this.animationExecuted = false;
                this.subscriptionService.hideSubscriptionOverlay();
            });
        } else {
            document.body.classList.remove('p-overflow-hidden');
            this.hideFeatureDetails('standard');
            this.hideFeatureDetails('advance');
            this.animationExecuted = false;
        }
        sessionStorage.setItem('subscriptionClosing', 'true');
    }

    // Verhindert dass Klicks auf den Inhalt das Overlay schließen
    onContentClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    // ngOnInit(): void {
    //     if (this.helper.hasRole(Role.BUYER)) {
    //         this.type = 'buyer';
    //     } else {
    //         this.type = 'supplier';
    //     }
    //
    //     // Neue Subscription für Overlay-Status
    //     this.subscription.add(
    //         this.subscriptionService.overlayVisible$.subscribe(visible => {
    //             if (visible) {
    //                 document.body.classList.add('p-overflow-hidden');
    //                 // Warte einen Moment, bis DOM aktualisiert ist
    //                 setTimeout(() => {
    //                     this.initializeSwipers();
    //                     this.runEntranceAnimation();
    //                 }, 100);
    //             } else {
    //                 document.body.classList.remove('p-overflow-hidden');
    //                 this.hideFeatureDetails('standard');
    //                 this.hideFeatureDetails('advance');
    //                 this.animationExecuted = false;
    //             }
    //         })
    //     );
    // }

    ngOnInit(): void {

        // Subscription für Overlay-Status
        this.subscription.add(
            this.subscriptionService.overlayVisible$.subscribe(visible => {
                if (visible) {
                    document.body.classList.add('p-overflow-hidden');
                    setTimeout(() => {
                        this.initializeSwipers();
                        this.runEntranceAnimation();
                    }, 100);
                } else {
                    document.body.classList.remove('p-overflow-hidden');
                    this.hideFeatureDetails('standard');
                    this.hideFeatureDetails('advance');
                    this.animationExecuted = false;
                }
            })
        );
    }


    ngAfterViewInit(): void {
    }

    // Eingangsanimation ausführen
    runEntranceAnimation(): void {
        // Nur ausführen, wenn Animation nicht bereits durchgeführt wurde
        if (this.animationExecuted) return;

        // Setze initial opacity auf 0 für den Container
        if (this.overlayContainer && this.overlayContainer.nativeElement) {
            this.overlayContainer.nativeElement.style.opacity = '0';
        }

        // Stelle sicher, dass die DOM-Elemente geladen sind
        setTimeout(() => {
            // Mache den Container sofort sichtbar
            if (this.overlayContainer && this.overlayContainer.nativeElement) {
                this.overlayContainer.nativeElement.style.opacity = '1';
                this.overlayContainer.nativeElement.style.transition = 'opacity 0.3s ease';
            }

            // Setze alle zu animierenden Elemente auf Ausgangszustand
            gsap.set('.overlay-container .inner', {opacity: 0, y: 20});
            gsap.set('.overlay-container .logo', {opacity: 0, y: -20});
            gsap.set('.overlay-container .headline', {opacity: 0});
            gsap.set('.overlay-container .description', {opacity: 0});
            gsap.set('.overlay-container .packages', {opacity: 0, y: 30});
            gsap.set('.overlay-container .overlay-footer-nav', {opacity: 0, y: 20});

            // Erstelle die Animation
            this.mainTimeline = gsap.timeline({
                defaults: {
                    ease: 'power2.out',
                    duration: 0.5
                }
            });

            // Füge die Animationsschritte hinzu
            this.mainTimeline
                .to('.overlay-container .inner', {opacity: 1, y: 0})
                .to('.overlay-container .logo', {opacity: 1, y: 0}, '-=0.3')
                .to('.overlay-container .headline', {opacity: 1}, '-=0.2')
                .to('.overlay-container .description', {opacity: 1}, '-=0.2')
                .to('.overlay-container .packages', {opacity: 1, y: 0, stagger: 0.15}, '-=0.2')
                .to('.overlay-container .overlay-footer-nav', {opacity: 1, y: 0}, '-=0.3');

            this.animationExecuted = true;
        }, 50);
    }

    // Feature-Detail-Animation starten
    animateFeatureDetailOpen(planType: 'standard' | 'advance'): void {
        // Selector für den richtigen Container
        const containerSelector = planType === 'standard'
            ? '.package:not(.advance) .feature-details-overlay'
            : '.package.advance .feature-details-overlay';

        // Animation
        gsap.fromTo(containerSelector,
            {opacity: 0, scale: 0.9},
            {opacity: 1, scale: 1, duration: 0.3, ease: 'power2.out'}
        );
    }

    // Feature-Detail-Animation beim Schließen
    animateFeatureDetailClose(planType: 'standard' | 'advance'): void {
        // Selector für den richtigen Container
        const containerSelector = planType === 'standard'
            ? '.package:not(.advance) .feature-details-overlay'
            : '.package.advance .feature-details-overlay';

        gsap.to(containerSelector, {
            opacity: 0,
            scale: 0.9,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
                // Diese Callback-Funktion wird aufgerufen, wenn die Animation abgeschlossen ist
                if (planType === 'standard') {
                    this.showStandardFeatureDetails = false;
                } else {
                    this.showAdvanceFeatureDetails = false;
                }

                // Nur den currentPlan zurücksetzen, wenn er dem aktuellen planType entspricht
                if (this.currentPlan === planType) {
                    this.currentPlan = null;
                    this.selectedFeatureId = null;
                }

                this.removeSwiperOpenClass(planType);
            }
        });
    }

    // Initialisiert die Swiper
    initializeSwipers(): void {

        setTimeout(() => {
            // Standard Swiper initialisieren
            if (this.standardFactsSwiper && this.showStandardFeatureDetails && this.currentPlan === 'standard') {
                if (this.standardSwiperInstance) {
                    this.standardSwiperInstance.destroy();
                }

                this.standardSwiperInstance = new Swiper(this.standardFactsSwiper.nativeElement, {
                    modules: [],
                    slidesPerView: 1,
                    spaceBetween: 10,
                    loop: true
                });

                // Index finden und zum Feature springen
                if (this.selectedFeatureId) {
                    // Finde den neuen Index im gefilterten Array
                    const includedFeatures = this.getIncludedFeatures('standard');
                    const featureIndex = includedFeatures.findIndex(f => f.id === this.selectedFeatureId);
                    if (featureIndex !== -1 && this.standardSwiperInstance) {
                        this.standardSwiperInstance.slideTo(featureIndex);
                    }
                }
            }

            // Advance Swiper initialisieren
            if (this.advanceFactsSwiper && this.showAdvanceFeatureDetails && this.currentPlan === 'advance') {
                if (this.advanceSwiperInstance) {
                    this.advanceSwiperInstance.destroy();
                }

                this.advanceSwiperInstance = new Swiper(this.advanceFactsSwiper.nativeElement, {
                    modules: [],
                    slidesPerView: 1,
                    spaceBetween: 10,
                    loop: true
                });

                // Index finden und zum Feature springen
                if (this.selectedFeatureId) {
                    // Finde den neuen Index im gefilterten Array
                    const includedFeatures = this.getIncludedFeatures('advance');
                    const featureIndex = includedFeatures.findIndex(f => f.id === this.selectedFeatureId);
                    if (featureIndex !== -1 && this.advanceSwiperInstance) {
                        this.advanceSwiperInstance.slideTo(featureIndex);
                    }
                }
            }
        }, 200);
    }

    // Feature-Details anzeigen und zum entsprechenden Slide navigieren
    showFeatureDetail(featureId: string, planType: 'standard' | 'advance'): void {
        this.selectedFeatureId = featureId;
        this.currentPlan = planType;

        // Setze den Zustand für die entsprechende Box
        if (planType === 'standard') {
            this.showStandardFeatureDetails = true;
        } else {
            this.showAdvanceFeatureDetails = true;
        }

        // Klasse zum Content-Container hinzufügen
        this.addSwiperOpenClass(planType);

        // Animation hinzufügen
        setTimeout(() => {
            this.animateFeatureDetailOpen(planType);
            this.initializeSwipers();
        }, 50);
    }

    // Feature-Details ausblenden für spezifischen Plan
    hideFeatureDetails(planType: 'standard' | 'advance'): void {
        // Animation zum Ausblenden starten
        if ((planType === 'standard' && this.showStandardFeatureDetails) ||
            (planType === 'advance' && this.showAdvanceFeatureDetails)) {
            this.animateFeatureDetailClose(planType);
        } else {
            // Falls keine Animation notwendig ist, direkt den Zustand zurücksetzen
            if (planType === 'standard') {
                this.showStandardFeatureDetails = false;
            } else {
                this.showAdvanceFeatureDetails = false;
            }

            // Nur den currentPlan zurücksetzen, wenn er dem aktuellen planType entspricht
            if (this.currentPlan === planType) {
                this.currentPlan = null;
                this.selectedFeatureId = null;
            }

            this.removeSwiperOpenClass(planType);
        }
    }

    // Prüft, ob eine Leistung in einem Tarif enthalten ist
    isFeatureIncluded(featureId: string, planType: 'standard' | 'advance'): boolean {
        const feature = this.features.find(f => f.id === featureId);
        if (!feature) return false;

        return feature.includedIn[planType];
    }

    // Manuelle Navigation für alle Fälle
    manualNext(planType: 'standard' | 'advance'): void {
        if (planType === 'standard' && this.standardSwiperInstance) {
            this.standardSwiperInstance.slideNext();
        } else if (planType === 'advance' && this.advanceSwiperInstance) {
            this.advanceSwiperInstance.slideNext();
        }
    }

    manualPrev(planType: 'standard' | 'advance'): void {
        if (planType === 'standard' && this.standardSwiperInstance) {
            this.standardSwiperInstance.slidePrev();
        } else if (planType === 'advance' && this.advanceSwiperInstance) {
            this.advanceSwiperInstance.slidePrev();
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    // Hilfsmethode, um die Klasse zum Content-Container hinzuzufügen
    private addSwiperOpenClass(planType: 'standard' | 'advance'): void {
        setTimeout(() => {
            // Wähle alle Content-Container über die DOM-Abfrage
            const contentContainers = document.querySelectorAll('.package .content');

            // Iteriere über alle gefundenen Container
            contentContainers.forEach((container) => {
                // Für den Standard-Plan
                if (planType === 'standard' && container.closest('.package:not(.advance)')) {
                    container.classList.add('swiper-open');
                }
                // Für den Advance-Plan
                else if (planType === 'advance' && container.closest('.package.advance')) {
                    container.classList.add('swiper-open');
                }
            });
        }, 10);
    }

    // Hilfsmethode, um die Klasse vom Content-Container zu entfernen
    private removeSwiperOpenClass(planType?: 'standard' | 'advance'): void {
        // Wenn kein planType angegeben ist, entferne die Klasse von allen Containern
        if (!planType) {
            const contentContainers = document.querySelectorAll('.package .content');
            contentContainers.forEach((container) => {
                container.classList.remove('swiper-open');
            });
            return;
        }

        // Sonst nur vom angegebenen Container
        setTimeout(() => {
            if (planType === 'standard') {
                const standardContainer = document.querySelector('.package:not(.advance) .content');
                if (standardContainer) {
                    standardContainer.classList.remove('swiper-open');
                }
            } else if (planType === 'advance') {
                const advanceContainer = document.querySelector('.package.advance .content');
                if (advanceContainer) {
                    advanceContainer.classList.remove('swiper-open');
                }
            }
        }, 10);
    }
}
